import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Button, Radio, Empty, Spin, Switch } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
// FLAT BUFFERS
import { ElevatorControl } from "../../../flatbuffers/generated/control/elevator-control";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
// ICONS
import elLockedDark from "../../../assets/icons/elevatorStatus/el-locked-dark.svg";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";

function ControlPanelBankHallCalls({
	hasFrontDoors,
	hasRearDoors,
	bankFloors,
	frontLock,
	rearLock,
}: {
	hasFrontDoors: boolean;
	hasRearDoors: boolean;
	bankFloors: BankFloorInterface[];
	frontLock: number[];
	rearLock: number[];
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [hallCallSelected, setHallCallSelected] = useState<"UP" | "DOWN">("UP");
	const [doorSelected, setDoorSelected] = useState<"FRONT" | "REAR">("FRONT");
	const [secureCalls, setSecureCalls] = useState<boolean>(false);
	const [showLoading, setShowLoading] = useState<boolean>(true);
	const [spinningFloorNo, setSpinningFloorNo] = useState<number>(0);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { permissions } = useSelector((state: RootState) => state.user);
	const { bankSelected } = useSelector((state: RootState) => state.monitoring);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();
	const hasBankFloorIndexZero = useMemo(
		() =>
			(bankSelected.bank_floors || []).some(
				(floor: BankFloorInterface) => floor.floor_index === 0
			),
		[bankSelected.bank_floors]
	);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickHallCall = async (floor: number) => {
		const shouldSubtractOne =
			bankSelected.start_floor_no === 1 && !hasBankFloorIndexZero;
		const floorToSend = Math.max(0, shouldSubtractOne ? floor - 1 : floor);
		let elevatorControl = null;
		if (secureCalls) {
			if (doorSelected === "FRONT") {
				if (hallCallSelected === "UP") {
					elevatorControl = ElevatorControl.UpHallCallLock;
				} else {
					elevatorControl = ElevatorControl.DownHallCallLock;
				}
			} else {
				// eslint-disable-next-line no-lonely-if
				if (hallCallSelected === "UP") {
					elevatorControl = ElevatorControl.UpRearHallCallLock;
				} else {
					elevatorControl = ElevatorControl.DownRearHallCallLock;
				}
			}
		} else {
			// eslint-disable-next-line no-lonely-if
			if (doorSelected === "FRONT") {
				if (hallCallSelected === "UP") {
					elevatorControl = ElevatorControl.EnergizeUpHallCall;
				} else {
					elevatorControl = ElevatorControl.EnergizeDownHallCall;
				}
			} else {
				// eslint-disable-next-line no-lonely-if
				if (hallCallSelected === "UP") {
					elevatorControl = ElevatorControl.EnergizeUpRearHallCall;
				} else {
					elevatorControl = ElevatorControl.EnergizeDownRearHallCall;
				}
			}
		}
		// POST COMMAND
		triggerPostCommand({
			floor: floorToSend,
			command: elevatorControl,
			active: true,
			liftType: "ELEVATOR",
		});
	};

	const onChangeSecureCalls = (checked: boolean) => {
		setSecureCalls(checked);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		setShowLoading(true);
		setTimeout(() => {
			setShowLoading(false);
		}, 500);
	}, []);

	useEffect(() => {
		if (!hasFrontDoors && hasRearDoors) {
			setDoorSelected("REAR");
		} else if (hasFrontDoors && !hasRearDoors) {
			setDoorSelected("FRONT");
		} else {
			setDoorSelected("FRONT");
		}
	}, [hasFrontDoors, hasRearDoors]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div
			className={`monitoringView__controlPanel__${theme}`}
			style={{ minHeight: "400px", height: "auto", padding: "20px" }}
		>
			<Spin spinning={showLoading}>
				<div>
					{!showLoading && (
						<>
							{(hasFrontDoors || hasRearDoors) && (
								<>
									{/** ACTIONS */}
									<div className={` drawer__box__control__panel__${theme} `}>
										<div className='generalStyles__spaceBetween'>
											<div className='generalStyles__flex'>
												<Radio.Group
													value={doorSelected}
													onChange={(e) => {
														setDoorSelected(e.target.value);
													}}
												>
													{hasFrontDoors && <Radio value='FRONT'>{t("general.frontDoors")}</Radio>}
													{hasRearDoors && <Radio value='REAR'>{t("general.rearDoors")}</Radio>}
												</Radio.Group>
											</div>
											{validatePermission("monitor-control-panel-secure-hall-calls", permissions) && (
												<div>
													{secureCalls && (
														<FontAwesomeIcon
															icon={faLock}
															fontSize={18}
															className='monitoring__modalSettings__icon generalStyles__mrFix'
														/>
													)}
													<span className='generalStyles__mrFix' style={{ marginTop: "5px" }}>
														{t("general.secureCalls")}
													</span>
													<Switch checked={secureCalls} onChange={onChangeSecureCalls} />
												</div>
											)}
										</div>
									</div>
									{/** ACTIONS */}
									<div className={` drawer__box__control__panel__${theme} mt-3`}>
										<div className='generalStyles__flex'>
											<div className='generalStyles__width100'>
												<div className='generalStyles__flex'>
													<Radio.Group
														value={hallCallSelected}
														onChange={(e) => {
															setHallCallSelected(e.target.value);
														}}
													>
														<Radio value='UP'>UP</Radio>
														<Radio value='DOWN'>DOWN</Radio>
													</Radio.Group>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
							{/** PANEL / CIRCLE BUTTONS */}
							<div className={`drawer__box__control__panel__${theme} mt-3`}>
								<div className='monitoringView__controlPanelPanel'>
									{hasFrontDoors || hasRearDoors ? (
										<>
											{bankFloors && bankFloors.slice()
												.sort(
													(a: BankFloorInterface, b: BankFloorInterface) =>
														b.floor_index - a.floor_index
												).map((floor: BankFloorInterface) => (
													<div key={floor.id}>
														<Button
															className='monitoring__modalSettings__button__1'
															shape='circle'
															onClick={() => {
																onClickHallCall(floor.floor_index);
																setSpinningFloorNo(floor.floor_index);
															}}
															disabled={
																(doorSelected === "FRONT" && !floor.front_door) ||
																(doorSelected === "REAR" && !floor.rear_door) ||
																(frontLock.includes(floor.floor_index) && doorSelected === "FRONT" && !secureCalls) ||
																(rearLock.includes(floor.floor_index) && doorSelected === "REAR" && !secureCalls)
															}
														>
															{(frontLock.includes(floor.floor_index) && doorSelected === "FRONT") ||
																(rearLock.includes(floor.floor_index) && doorSelected === "REAR") ? (
																<img src={elLockedDark} alt='' />
															) : (
																<>
																	{isLoadingPostCommand && spinningFloorNo === floor.floor_index ? (
																		<Spin spinning={isLoadingPostCommand && spinningFloorNo === floor.floor_index} />
																	) : (
																		<span style={{ marginTop: "3px" }}>
																			{floor && floor.name && floor.name !== ""
																				? floor.name.substring(0, 3).toUpperCase()
																				: floor.floor_index}
																		</span>
																	)}
																</>
															)}
														</Button>{" "}
													</div>
												))}
										</>
									) : (
										<Empty description={<span>{t("general.bankHasNoFrontRearDoors")}</span>} />
									)}
								</div>
							</div>
						</>
					)}
				</div>
			</Spin>
		</div>
	);
}

export default ControlPanelBankHallCalls;
